import React, { useEffect, useState } from "react"
import {
  Autocomplete,
  Button,
  FormControl,
  TextField,
  Typography
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import { useSelector, useDispatch } from "react-redux"
import {
  setDescription,
  setStreet,
  setZip,
  setCity,
  setCoordinates,
  unsetCoordinates,
  setNumber,
  setPerson,
  setPhone,
  setCommission,
  setUsersOrderNumber,
  setRemarks
} from "../../../features/project/deliverySlice"

import { orangeButtonStyle } from "../../../constants"
import { constructionSiteApiActions } from "../../../actions"
import InfoIcon from "@mui/icons-material/Info"
import { Maps } from "./Maps"
import { CityItem } from "../City"

const TitleItem = () => {
  return (
    <React.Fragment>
      <Typography component="h1" variant="h4">
        Baustelle/Lieferung
      </Typography>
      <hr />
    </React.Fragment>
  )
}

const PresetSelectGridItem = () => {
  const dispatch = useDispatch()
  const constructionSites = useSelector(state => state.constructionSites)

  const toOptions = constructionSites =>
    Array.isArray(constructionSites)
      ? constructionSites.map(c => {
          return {
            label: `${c.description}, ${c.street}, ${c.zip} ${c.city}`,
            value: c.projectId
          }
        })
      : []

  const handleInputChange = (event, value) => {
    dispatch(constructionSiteApiActions.readConstructionSites(5, value))
  }

  const handleChange = (event, value) => {
    if (!value) {
      return
    }
    const site = constructionSites.find(cs => cs.projectId === value.value)

    if (!!site) {
      dispatch(setDescription(site.description))
      dispatch(setStreet(site.street))
      dispatch(setZip(site.zip))
      dispatch(setCity(site.city))
      dispatch(setNumber(site.number))
      dispatch(setPerson(site.person))
      dispatch(setPhone(site.phone))
      dispatch(setCommission(site.commission))
      dispatch(setUsersOrderNumber(site.usersOrderNumber))
      dispatch(setRemarks(site.remarks))
      dispatch(setCoordinates(site.latitude, site.longitude))
    }
  }

  useEffect(() => {
    dispatch(constructionSiteApiActions.readConstructionSites())
  }, [dispatch])

  return (
    <Grid size={12}>
      <FormControl fullWidth variant="outlined">
        <Autocomplete
          options={toOptions(constructionSites)}
          getOptionLabel={option => option.label.toString()}
          renderInput={params => (
            <TextField
              {...params}
              label="Suchbegriff eingeben..."
              variant="outlined"
              fullWidth
            />
          )}
          onChange={handleChange}
          onInputChange={handleInputChange}
        />
      </FormControl>
    </Grid>
  )
}

const LoginForPresetsTextGridItem = () => {
  return (
    <Grid size={12}>
      <Grid direction={"row"} container alignItems={"center"}>
        <Grid>
          <InfoIcon style={{ marginRight: "1em" }} />
        </Grid>
        <Grid size={10}>
          <Typography component="p" variant="body1">
            Sie müssen angemeldet sein um Angaben aus bereits erfassten
            Baustellen zu übernehmen.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

const PresetItem = () => {
  const authentication = useSelector(state => state.authentication)

  return (
    <Grid container spacing={2}>
      {!!authentication.loggedIn && <PresetSelectGridItem />}
      {!authentication.loggedIn && <LoginForPresetsTextGridItem />}
    </Grid>
  )
}

const DescriptionItem = () => {
  const dispatch = useDispatch()
  const description = useSelector(state => state.project.delivery.description)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      id="description"
      label="Baustellenbezeichnung"
      name="description"
      value={description}
      error={!description}
      helperText={
        !description ? "Baustellenbezeichnung ist erforderlich" : null
      }
      onChange={e => {
        dispatch(setDescription(e.target.value))
      }}
    />
  )
}

const StreetItem = () => {
  const dispatch = useDispatch()
  const street = useSelector(state => state.project.delivery.street)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      id="street"
      label="Strasse und Nummer"
      name="street"
      value={street}
      error={!street}
      helperText={!street ? "Strasse ist erforderlich" : null}
      onChange={e => {
        dispatch(setStreet(e.target.value))
      }}
    />
  )
}

const NumberItem = () => {
  const dispatch = useDispatch()
  const number = useSelector(state => state.project.delivery.number)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="number"
      label="Baustellennummer"
      name="number"
      value={number}
      onChange={e => {
        dispatch(setNumber(e.target.value))
      }}
    />
  )
}

const PersonItem = () => {
  const dispatch = useDispatch()
  const person = useSelector(state => state.project.delivery.person)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="person"
      label="Name Kontaktperson auf Baustelle"
      name="person"
      value={person}
      onChange={e => {
        dispatch(setPerson(e.target.value))
      }}
    />
  )
}

const PhoneItem = () => {
  const dispatch = useDispatch()
  const phone = useSelector(state => state.project.delivery.phone)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="phone"
      label="Telefon Kontaktperson auf Baustelle"
      name="phone"
      value={phone}
      onChange={e => {
        dispatch(setPhone(e.target.value))
      }}
    />
  )
}

const CommissionItem = () => {
  const dispatch = useDispatch()
  const commission = useSelector(state => state.project.delivery.commission)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="commission"
      label="Kommission"
      name="commission"
      value={commission}
      onChange={e => {
        dispatch(setCommission(e.target.value))
      }}
    />
  )
}

const UsersOrderNumberItem = () => {
  const dispatch = useDispatch()
  const usersOrderNumber = useSelector(state => state.project.delivery.usersOrderNumber)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="usersOrderNumber"
      label="Ihre Bestell-/Auftragsnummer"
      name="commission"
      value={usersOrderNumber}
      onChange={e => {
        dispatch(setUsersOrderNumber(e.target.value))
      }}
    />
  )
}

const RemarksItem = () => {
  const dispatch = useDispatch()
  const remarks = useSelector(state => state.project.delivery.remarks)

  return (
    <TextField
      margin="normal"
      variant="outlined"
      multiline
      minRows={7}
      fullWidth
      sx={{ fontStyle: 'italic' }}
      id="remarks"
      label="Bemerkungen"
      name="remarks"
      value={remarks}
      onChange={e => {
        dispatch(setRemarks(e.target.value))
      }}
    />
  )
}

const MapItem = () => {
  const street = useSelector(state => state.project.delivery.street)
  const zip = useSelector(state => state.project.delivery.zip)
  const city = useSelector(state => state.project.delivery.city)

  return (
    <Grid size={12}>
      <Maps address={street + ", " + zip + " " + city + ", Schweiz"}></Maps>
    </Grid>
  )
}

export const ConstructionSiteSection = () => {
  const [showMap, setShowMap] = useState(false)
  const dispatch = useDispatch()
  const street = useSelector(state => state.project.delivery.street)
  const zip = useSelector(state => state.project.delivery.zip)
  const city = useSelector(state => state.project.delivery.city)
  const latitude = useSelector(state => state.project.delivery.latitude)
  const longitude = useSelector(state => state.project.delivery.longitude)

  return (
    <Grid container spacing={6}>
      <Grid size={12}>
        <TitleItem />
      </Grid>
      <Grid size={6}>
        <Grid container>
          <Grid size={12} key={"upper_text"}>
            <Typography component="p" variant="body1">
              Wählen Sie eine bereits erfasste Baustelle aus, um die Angaben zu
              übernehmen.
            </Typography>
          </Grid>
          <Grid
            size={12}
            style={{ marginBottom: "5.5rem", marginTop: "1rem" }}
          >
            <PresetItem />
          </Grid>

          <Grid size={12}>
            <DescriptionItem />
          </Grid>
          <Grid size={12}>
            <StreetItem />
          </Grid>
          <Grid size={12}>
            <CityItem
              setZipAction={setZip} setCityAction={setCity}
              selectZip={state => state.project.delivery.zip} selectCity={state => state.project.delivery.city}
            />
          </Grid>

          {!showMap && street && zip && city && (
            <Grid size={12} marginTop={"0.7rem"}>
              <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography>
                  Optional: Markieren Sie den genauen Abladeort.
                </Typography>
                <Button
                  sx={orangeButtonStyle}
                  color="primary"
                  variant="contained"
                  onClick={() => setShowMap(true)}
                >
                  Karte einblenden
                </Button>
              </Grid>
            </Grid>
          )}
          {showMap && (!!latitude || !!longitude) && (
            <Grid size={12} marginTop={"0.7rem"}>
              <Grid container justifyContent={"flex-end"} alignItems={"center"}>
                <Button
                  sx={orangeButtonStyle}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    dispatch(unsetCoordinates())
                    setShowMap(false)
                  }}
                >
                  Markierung auf Karte löschen
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid size={6}>
        <Grid container>
          <Grid size={12} key={"upper_text"}>
            <Typography component="p" variant="body1">
              Optionale Eingabefelder
            </Typography>
          </Grid>
          <Grid size={12}>
            <NumberItem />
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <PersonItem />
              </Grid>
              <Grid size={6}>
                <PhoneItem />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <CommissionItem />
              </Grid>
              <Grid size={6}>
                <UsersOrderNumberItem />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <RemarksItem />
          </Grid>
        </Grid>
      </Grid>

      {showMap && (
        <Grid size={12}>
          <MapItem />
        </Grid>
      )}
    </Grid>
  )
}
